import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProject } from '../../../contexts/ProjectContext';
import { useWorkspace } from '../../../contexts/WorkspaceContext';
import {
  getProjectDetail,
  uploadProjectDocuments,
  deleteProjectDocument,
  updateProject
} from '../../../services/projectService';
import { createChatSession, deleteSession } from '../../../services/chatService';
import * as agentService from '../../../services/agentService';
import { ProjectDetail, ProjectDocument } from '../../../types/project';
import { Agent } from '../../../types/agent';

interface UseProjectDetailOptions {
  projectId: string | undefined;
}

/**
 * useProjectDetail - Hook para gerenciar detalhes de um projeto
 *
 * Encapsula toda a lógica de:
 * - Carregar detalhes do projeto
 * - Gerenciar agentes
 * - Criar/deletar conversas
 * - Upload/delete de documentos
 */
export const useProjectDetail = ({ projectId }: UseProjectDetailOptions) => {
  const navigate = useNavigate();
  const { currentProject, setActiveProject } = useProject();
  const { currentWorkspace } = useWorkspace();

  // Project state
  const [projectDetail, setProjectDetail] = useState<ProjectDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Agent state
  const [agents, setAgents] = useState<Agent[]>([]);
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  const [isLoadingAgents, setIsLoadingAgents] = useState(false);

  // Action states
  const [isCreatingChat, setIsCreatingChat] = useState(false);
  const [isDeletingChat, setIsDeletingChat] = useState(false);
  const [isUploadingFiles, setIsUploadingFiles] = useState(false);
  const [isDeletingFile, setIsDeletingFile] = useState(false);
  const [isUpdatingInstructions, setIsUpdatingInstructions] = useState(false);

  const AGENTS_LIMIT = 10;

  // Activate project on mount
  useEffect(() => {
    if (!projectId) {
      setIsLoading(false);
      return;
    }

    const activateProject = async () => {
      setIsLoading(true);
      setError(null);

      try {
        if (currentProject?.id !== projectId) {
          await setActiveProject(projectId);
        }
      } catch (err) {
        console.error('Failed to activate project:', err);
        setError('Falha ao carregar projeto');
        navigate('/projetos');
      }
    };

    activateProject();
  }, [projectId]);

  // Load project detail when project is active
  const loadProjectDetail = useCallback(async () => {
    if (!currentProject?.id) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await getProjectDetail(currentProject.id);
      if (response.success && response.data) {
        setProjectDetail(response.data);
      }
    } catch (err) {
      console.error('Error loading project detail:', err);
      setError(err instanceof Error ? err.message : 'Falha ao carregar detalhes do projeto');
    } finally {
      setIsLoading(false);
    }
  }, [currentProject?.id]);

  useEffect(() => {
    if (currentProject?.id) {
      loadProjectDetail();
    }
  }, [currentProject?.id, loadProjectDetail]);

  // Load agents
  useEffect(() => {
    const loadAgents = async () => {
      try {
        setIsLoadingAgents(true);
        const response = await agentService.getAvailableAgents(0, AGENTS_LIMIT);

        if (response.success) {
          setAgents(response.data);

          // Set initial agent (não sobrescreve seleção já feita, ex.: retorno do catálogo)
          if (response.data.length > 0) {
            const lastUsedAgentId = localStorage.getItem('lastUsedAgentId');
            const lastAgent = lastUsedAgentId
              ? response.data.find((agent: Agent) => agent.id === parseInt(lastUsedAgentId))
              : undefined;
            setSelectedAgent(prev => prev ?? (lastAgent || response.data[0] || null));
          }
        }
      } catch (err) {
        console.error('Error loading agents:', err);
      } finally {
        setIsLoadingAgents(false);
      }
    };

    loadAgents();
  }, []);

  // Create new chat in project
  const createChat = useCallback(async (question: string, files?: File[]) => {
    if (!selectedAgent || !currentProject?.id) return;

    setIsCreatingChat(true);

    try {
      // Upload files if provided
      if (files && files.length > 0) {
        const uploadResponse = await uploadProjectDocuments(currentProject.id, files);
        if (uploadResponse.success && uploadResponse.data) {
          setProjectDetail(prev => {
            if (!prev) return prev;
            return {
              ...prev,
              documents: [...uploadResponse.data, ...(prev.documents || [])]
            };
          });
        }
      }

      // Create session
      // ✅ CRITICAL: Pass workspace_id for proper token accounting
      const sessionData = await createChatSession(
        selectedAgent.id,
        currentProject.id,
        currentWorkspace?.id
      );
      const sessionId = sessionData.data.id;

      // Store pending question
      localStorage.setItem('pendingChatQuestion', question);
      localStorage.setItem('lastUsedAgentId', selectedAgent.id.toString());

      // Navigate to chat
      navigate(`/project/${currentProject.id}/chat/${sessionId}`);
    } catch (err) {
      console.error('Error creating chat:', err);
      throw err;
    } finally {
      setIsCreatingChat(false);
    }
  }, [selectedAgent, currentProject?.id, navigate]);

  // Delete chat
  const deleteChat = useCallback(async (chatId: number) => {
    setIsDeletingChat(true);

    try {
      await deleteSession(chatId);
      await loadProjectDetail();
    } catch (err) {
      console.error('Error deleting chat:', err);
      throw err;
    } finally {
      setIsDeletingChat(false);
    }
  }, [loadProjectDetail]);

  // Upload files
  const uploadFiles = useCallback(async (files: File[]) => {
    if (!currentProject?.id) return;

    setIsUploadingFiles(true);

    try {
      const response = await uploadProjectDocuments(currentProject.id, files);
      if (response.success && response.data) {
        setProjectDetail(prev => {
          if (!prev) return prev;
          const newIds = new Set(response.data.map((doc: ProjectDocument) => doc.id));
          return {
            ...prev,
            documents: [
              ...response.data,
              ...(prev.documents?.filter((doc) => !newIds.has(doc.id)) || [])
            ]
          };
        });
      }
    } catch (err) {
      console.error('Error uploading files:', err);
      throw err;
    } finally {
      setIsUploadingFiles(false);
    }
  }, [currentProject?.id]);

  // Delete file
  const deleteFile = useCallback(async (documentId: number) => {
    if (!currentProject?.id) return;

    setIsDeletingFile(true);

    try {
      await deleteProjectDocument(currentProject.id, documentId);
      setProjectDetail(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          documents: prev.documents.filter(doc => doc.id !== documentId)
        };
      });
    } catch (err) {
      console.error('Error deleting file:', err);
      throw err;
    } finally {
      setIsDeletingFile(false);
    }
  }, [currentProject?.id]);

  // Update custom instructions
  const updateInstructions = useCallback(async (instructions: string) => {
    if (!currentProject?.id) return;

    setIsUpdatingInstructions(true);

    try {
      await updateProject(currentProject.id, { custom_instructions: instructions });

      // Update local state
      setProjectDetail(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          custom_instructions: instructions
        };
      });
    } catch (err) {
      console.error('Error updating instructions:', err);
      throw err;
    } finally {
      setIsUpdatingInstructions(false);
    }
  }, [currentProject?.id]);

  return {
    // Project data
    project: currentProject,
    projectDetail,
    isLoading,
    error,

    // Agents
    agents,
    selectedAgent,
    setSelectedAgent,
    isLoadingAgents,

    // Actions
    createChat,
    isCreatingChat,
    deleteChat,
    isDeletingChat,
    uploadFiles,
    isUploadingFiles,
    deleteFile,
    isDeletingFile,
    updateInstructions,
    isUpdatingInstructions,

    // Refresh
    refresh: loadProjectDetail
  };
};

export type { ProjectDetail, ProjectDocument };
